import { useSyncExternalStore } from 'react';
import { tableEditingStore, DeferredRowUpdate } from '@/store/table-editing.store';

/**
 * Hook to check if a specific row has pending deferred updates.
 *
 * This is useful for showing visual indicators when a row has
 * updates that are waiting to be applied after a popover closes.
 *
 * @param rowId - The row ID to check for pending updates
 * @returns The deferred update if present, undefined otherwise
 */
export function usePendingRowUpdate(rowId: string): DeferredRowUpdate | undefined {
  return useSyncExternalStore(
    tableEditingStore.subscribe,
    () => tableEditingStore.getState().getDeferredUpdate(rowId),
    () => undefined
  );
}

/**
 * Hook to check if a row has any pending deferred updates.
 *
 * @param rowId - The row ID to check
 * @returns true if the row has pending updates
 */
export function useHasPendingRowUpdate(rowId: string): boolean {
  return useSyncExternalStore(
    tableEditingStore.subscribe,
    () => tableEditingStore.getState().hasDeferredUpdate(rowId),
    () => false
  );
}

/**
 * Hook to check if a row has any open popovers.
 *
 * @param rowId - The row ID to check
 * @returns true if the row has any open popovers
 */
export function useRowHasOpenPopover(rowId: string): boolean {
  return useSyncExternalStore(
    tableEditingStore.subscribe,
    () => tableEditingStore.getState().hasOpenPopoverForRow(rowId),
    () => false
  );
}
